import { useTranslation } from 'react-i18next';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useObligationEvents } from '../../services/stateTaxClientsApi';
import {
  formatObligationEventTime,
  formatDeltaValue,
} from '../../utils/filingActivityHelpers';
import type { ObligationEvent } from '../../types';

/**
 * F2 — Lifecycle audit trail for one obligation. Mounted inside a Popover
 * that triggers off the Active/Inactive status badge on each obligation row.
 * Empty state still surfaces the synthesized "created" entry when Dataverse
 * EffectiveStartDate is set, so a brand-new obligation isn't a blank popover.
 */
const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: '320px',
    maxWidth: '420px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  heading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalXS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:first-child': {
      borderTop: 'none',
      paddingTop: 0,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  time: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  delta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  reason: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

export function ObligationHistoryPopover({
  stateTaxClientId,
  obligationId,
}: {
  stateTaxClientId: string;
  obligationId: string;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data, isLoading, isError } = useObligationEvents(stateTaxClientId, obligationId);

  const events = data ?? [];

  return (
    <div className={styles.list}>
      <Text className={styles.heading}>
        {t('clientDetailPage.obligationHistory.title')}
      </Text>
      <Text className={styles.subtitle} block>
        {t('clientDetailPage.obligationHistory.subtitle')}
      </Text>

      {isLoading && <Spinner size="tiny" label={t('clientDetailPage.obligationHistory.loading')} />}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{t('clientDetailPage.obligationHistory.loadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && events.length === 0 && (
        <Text className={styles.emptyHint}>
          {t('clientDetailPage.obligationHistory.empty')}
        </Text>
      )}

      {events.map((ev: ObligationEvent) => {
        const beforeKeys = Object.keys(ev.before ?? {});
        const afterKeys = Object.keys(ev.after ?? {});
        // Use the union of keys so a "created" event (empty before, populated
        // after) still renders its after-snapshot rather than nothing.
        const deltaKeys = Array.from(new Set([...beforeKeys, ...afterKeys]));
        const attribution = ev.changedByName?.trim()
          || (ev.changedById?.trim() ?? '')
          || t('clientDetailPage.obligationHistory.unknownActor');

        return (
          <div key={ev.id} className={styles.row}>
            <div className={styles.header}>
              <Text style={{ fontWeight: 600, fontSize: tokens.fontSizeBase300 }}>
                {t(`clientDetailPage.obligationHistory.eventType.${ev.eventType}`, {
                  defaultValue: ev.eventType,
                })}
              </Text>
              <Text className={styles.time}>
                {formatObligationEventTime(ev.changedAt)}
              </Text>
            </div>
            <Text className={styles.delta}>
              {t('clientDetailPage.obligationHistory.byActor', { name: attribution })}
            </Text>
            {/* Binary active-flip events (deactivated / reactivated / removed) skip the
                delta rows — the label already carries the whole meaning. */}
            {deltaKeys.length > 0 && ev.eventType !== 'deactivated' && ev.eventType !== 'reactivated' && ev.eventType !== 'removed' && (
              <div>
                {deltaKeys.map((key) => {
                  const before = (ev.before ?? {})[key];
                  const after = (ev.after ?? {})[key];
                  return (
                    <Text key={key} className={styles.delta} block>
                      {ev.eventType === 'created'
                        ? `${key}: ${formatDeltaValue(after)}`
                        : t('clientDetailPage.obligationHistory.fromTo', {
                            field: key,
                            before: formatDeltaValue(before),
                            after: formatDeltaValue(after),
                          })}
                    </Text>
                  );
                })}
              </div>
            )}
            {ev.periodLabel?.trim() && (
              <Text className={styles.delta} block>
                {t('clientDetailPage.obligationHistory.forPeriod', { period: ev.periodLabel.trim() })}
              </Text>
            )}
            {ev.reason?.trim() && (
              <Text className={styles.reason}>
                {t('clientDetailPage.obligationHistory.reason', { reason: ev.reason.trim() })}
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
}
